import { useMemo } from 'react';
import clsx from 'clsx';

// Clickable per-event-type count chips for the webhook-deliveries tab (DR-2327),
// scoped to the chart's current time window. Replaces the old event-type
// dropdown: every type that occurred in the window gets a chip showing its
// count; clicking one filters the chart + table to that type, clicking it again
// (or `All`) clears the filter.
export function WebhookEventTypeChips({
  eventTypeCounts,
  selected,
  onSelect,
  dimmed,
}: {
  eventTypeCounts: Record<string, number>;
  selected: string;
  onSelect: (eventType: string) => void;
  dimmed: boolean;
}) {
  const { total, chips } = useMemo(() => {
    const entries = Object.entries(eventTypeCounts).filter(([, count]) => count > 0);
    // Keep the current selection escapable: if it has no deliveries in the new
    // window, still render its chip (count 0) so the user can click off it.
    if (selected && !(selected in eventTypeCounts)) {
      entries.push([selected, 0]);
    }
    // Count desc, then event-type name for a stable order on ties.
    entries.sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return {
      total: entries.reduce((sum, [, count]) => sum + count, 0),
      chips: entries,
    };
  }, [eventTypeCounts, selected]);

  const chipClass = (active: boolean) =>
    clsx(
      'rounded-full border px-2.5 py-1 text-xs transition-colors',
      active
        ? 'border-indigo-500 bg-indigo-500 text-white'
        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-600',
    );

  return (
    <div className={clsx('flex flex-col gap-2 transition-opacity', dimmed && 'opacity-40')}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400 dark:text-zinc-500">
        Events in window
      </span>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onSelect('')} className={chipClass(selected === '')}>
          All <span className="font-semibold tabular-nums">{total}</span>
        </button>
        {chips.map(([eventType, count]) => (
          <button
            key={eventType}
            type="button"
            // Clicking the active chip clears the filter (back to `All`).
            onClick={() => onSelect(selected === eventType ? '' : eventType)}
            className={chipClass(selected === eventType)}
          >
            {eventType} <span className="font-semibold tabular-nums">{count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
